import React from 'react';
import { StudioIcon } from '../design/StudioShell';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  LinearProgress,
  Pagination,
  Snackbar,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import { Parse } from 'parse';
import {
  activeTransport,
  apiCapabilities,
  lifeTaggerApi,
  syncLifeObjectAnchors,
} from '../api/lifetaggerApi';
import { lifeTaggerDesign } from '../design/tokens';
import ObjectEditor from './ObjectEditor';
import EditorialCollectionStream from './EditorialCollectionStream';
import LifeObjectPreviewOverlay from './LifeObjectPreviewOverlay';
import { lifeTaggerTheme } from '../design/LifeTaggerTheme';
import { runtimeReadiness } from '../api/runtimeReadiness';
import { deliverPublishedObject } from './objectPushDelivery';

const meaningfulTimestamp = (item) => {
  const metadataDate =
    item.metadata?.sourceUpdatedAt || item.metadata?.sourceCreatedAt;
  if (metadataDate) return new Date(metadataDate).getTime() || 0;
  if (item.metadata?.sourceMessageId) return 0;
  return new Date(item.updatedAt || item.createdAt || 0).getTime() || 0;
};

const hasUnavailableMedia = (item) =>
  item.content?.some((entry) =>
    /(?:^|\.)lifetagger\.s3\.amazonaws\.com/i.test(
      entry.url || entry.assetPreviewUrl || ''
    )
  );

const isScheduled = (item) => {
  const start = item.lifecycle?.startsAt || item.lifecycle?.startAt;
  return Boolean(start && new Date(start).getTime() > Date.now());
};

const matchesCollectionFilter = (item, filter) => {
  if (filter === 'all') return true;
  if (filter === 'scheduled') return isScheduled(item);
  if (filter === 'attention') return hasUnavailableMedia(item);
  return item.lifecycle?.status === filter;
};

export default function ObjectWorkspace({ location, history }) {
  const [view, setView] = React.useState('grid');
  const [search, setSearch] = React.useState('');
  const [objects, setObjects] = React.useState([]);
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [serverPage, setServerPage] = React.useState(1);
  const [hasMoreFromServer, setHasMoreFromServer] = React.useState(false);
  const [serverTotal, setServerTotal] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loadMoreError, setLoadMoreError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [editor, setEditor] = React.useState({ open: false, lifeObject: null });
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [sort, setSort] = React.useState('updated-desc');
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState('');
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const deleteObject = async () => {
    if (!deleteTarget || saving) return;
    setSaving(true);
    setError(null);
    try {
      await lifeTaggerApi.deleteLifeObject(deleteTarget.id);
      setObjects((items) =>
        items.filter((item) => item.id !== deleteTarget.id)
      );
      setSelected((item) => (item?.id === deleteTarget.id ? null : item));
      setSelectedIds((ids) => ids.filter((id) => id !== deleteTarget.id));
      setServerTotal((total) =>
        total == null ? total : Math.max(0, total - 1)
      );
      setDeleteTarget(null);
      setNotice('LifeObject deleted.');
    } catch (err) {
      setError(
        err.message || 'Could not delete this LifeObject. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };
  const [page, setPage] = React.useState(1);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [selectionMode, setSelectionMode] = React.useState(false);
  const serverBatchSize = 24;
  const pageSize = 12;

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setEditor({
        open: true,
        lifeObject: null,
        initialAsset: location.state?.asset,
        initialDiscovery: params.get('delivery') === 'push' ? 'push' : null,
      });
      history.replace('/objects');
    }
    const preview = params.get('preview');
    if (preview) {
      let active = true;
      lifeTaggerApi
        .getLifeObject(preview)
        .then((item) => active && setSelected(item))
        .catch((err) => active && setError(err.message));
      return () => {
        active = false;
      };
    }
  }, [location.search, history]);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    lifeTaggerApi
      .listLifeObjectsPage({
        includeArchived: false,
        page: 1,
        limit: serverBatchSize,
        search: debouncedSearch,
      })
      .then(({ items, hasMore, total }) => {
        if (!active) return;
        setObjects(items);
        setServerPage(1);
        setHasMoreFromServer(hasMore);
        setServerTotal(total);
        setSelected(
          (current) => items.find((item) => item.id === current?.id) || null
        );
      })
      .catch((requestError) => active && setError(requestError.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [debouncedSearch, reloadKey]);

  const loadMoreFromServer = async () => {
    setLoading(true);
    setError(null);
    setLoadMoreError(null);
    try {
      const nextPage = serverPage + 1;
      const result = await lifeTaggerApi.listLifeObjectsPage({
        includeArchived: false,
        page: nextPage,
        limit: serverBatchSize,
        search: debouncedSearch,
      });
      setObjects((current) => {
        const byId = new Map(current.map((item) => [item.id, item]));
        result.items.forEach((item) => byId.set(item.id, item));
        return [...byId.values()];
      });
      setServerPage(nextPage);
      setHasMoreFromServer(result.hasMore);
      setServerTotal(result.total);
    } catch (loadError) {
      setLoadMoreError(loadError.message);
    } finally {
      setLoading(false);
    }
  };

  const saveObject = async (draft, delivery) => {
    setSaving(true);
    let persisted;
    try {
      const saved =
        draft.id || editor.lifeObject?.id
          ? await lifeTaggerApi.updateLifeObject(
              draft.id || editor.lifeObject.id,
              draft
            )
          : await lifeTaggerApi.createLifeObject(draft);
      persisted = saved;
      const anchors = await syncLifeObjectAnchors({
        lifeObjectId: saved.id,
        previous: editor.lifeObject?.anchors || [],
        next: draft.anchors || [],
      });
      const savedWithAnchors = { ...saved, anchors };
      const notification = await deliverPublishedObject(
        savedWithAnchors,
        delivery,
        (name, params) => Parse.Cloud.run(name, params)
      );
      // A list refresh failure must not turn a completed send into a failed
      // publish. The saved object can update the current collection directly.
      const refreshed = await lifeTaggerApi
        .listLifeObjects({ search, includeArchived: false })
        .catch(() => null);
      setObjects((current) => {
        const items = refreshed || current;
        return items.some((item) => item.id === savedWithAnchors.id)
          ? items.map((item) =>
              item.id === savedWithAnchors.id ? savedWithAnchors : item
            )
          : [savedWithAnchors, ...items];
      });
      setSelected(savedWithAnchors);
      setEditor({ open: false, lifeObject: null });
      setNotice(
        notification?.status === 'scheduled'
          ? 'LifeObject published. Notification scheduled.'
          : notification?.status === 'accepted'
          ? `LifeObject published. Notification accepted for ${notification.accepted} browsers.`
          : notification?.status === 'partial'
          ? `LifeObject published. Notification accepted for ${notification.accepted} browsers; ${notification.failed} failed.`
          : draft.lifecycle.status === 'active'
          ? 'LifeObject published.'
          : 'Draft saved.'
      );
    } catch (saveError) {
      setError(saveError.message);
      if (persisted) saveError.savedObject = persisted;
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const previewIdentifier = selected?.anchors?.find(
    (anchor) => anchor.type === 'qr'
  )?.identifier;
  const visibleObjects = objects.filter((item) =>
    matchesCollectionFilter(item, statusFilter)
  );
  const sortedObjects = [...visibleObjects].sort((left, right) => {
    if (sort === 'name-asc') return left.name.localeCompare(right.name);
    if (sort === 'name-desc') return right.name.localeCompare(left.name);
    const leftTime = meaningfulTimestamp(left);
    const rightTime = meaningfulTimestamp(right);
    return sort === 'updated-asc' ? leftTime - rightTime : rightTime - leftTime;
  });
  const pageCount = Math.max(1, Math.ceil(visibleObjects.length / pageSize));
  React.useEffect(() => {
    setPage((current) => Math.min(current, pageCount));
  }, [pageCount]);
  const pageObjects = sortedObjects.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  const collectionTotal = serverTotal ?? objects.length;
  const hasCompleteCollection =
    !hasMoreFromServer &&
    (serverTotal == null || objects.length >= serverTotal);
  const lifecycleCounts = objects.reduce(
    (counts, item) => ({
      ...counts,
      [item.lifecycle.status]: (counts[item.lifecycle.status] || 0) + 1,
    }),
    {}
  );
  const scheduledCount = objects.filter(isScheduled).length;
  const attentionCount = objects.filter(hasUnavailableMedia).length;
  const statusOptions = [
    ['all', 'All'],
    ['active', 'Published'],
    ['draft', 'Draft'],
    ['scheduled', 'Scheduled'],
    ['attention', 'Attention'],
  ];
  const filterCount = (value) => {
    if (value === 'all') return collectionTotal;
    if (!hasCompleteCollection) return null;
    if (value === 'scheduled') return scheduledCount;
    if (value === 'attention') return attentionCount;
    return lifecycleCounts[value] || 0;
  };
  const sortOptions = [
    ['updated-desc', 'Recently edited'],
    ['updated-asc', 'Oldest updated'],
    ['name-asc', 'Name A–Z'],
    ['name-desc', 'Name Z–A'],
  ];

  React.useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const toggleBulkSelection = (id) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id]
    );
  };

  const updateSelectedStatus = async (status) => {
    if (!selectedIds.length) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        selectedIds.map((id) => {
          const item = objects.find((candidate) => candidate.id === id);
          return lifeTaggerApi.updateLifeObject(id, {
            lifecycle: { ...item.lifecycle, status },
          });
        })
      );
      const refreshed = await lifeTaggerApi.listLifeObjects({
        search,
        includeArchived: false,
      });
      setObjects(refreshed);
      setSelected(
        (current) =>
          refreshed.find((item) => item.id === current?.id) ||
          refreshed[0] ||
          null
      );
      setSelectedIds([]);
      setNotice(
        `${selectedIds.length} LifeObject${
          selectedIds.length === 1 ? '' : 's'
        } updated.`
      );
    } catch (bulkError) {
      setError(bulkError.message);
    } finally {
      setSaving(false);
    }
  };

  const duplicateSelected = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const duplicate = JSON.parse(JSON.stringify(selected));
      delete duplicate.id;
      delete duplicate.objectId;
      delete duplicate.createdAt;
      delete duplicate.updatedAt;
      duplicate.name = `${selected.name} copy`;
      duplicate.lifecycle = {
        ...duplicate.lifecycle,
        status: 'draft',
      };
      // A copy starts unattached so it cannot accidentally compete with the
      // original LifeObject for the same LifeCode, place, or recognized signal.
      duplicate.anchors = [];
      duplicate.resolvedVia = [];
      const created = await lifeTaggerApi.createLifeObject(duplicate);
      setObjects(
        await lifeTaggerApi.listLifeObjects({ search, includeArchived: false })
      );
      setSelected(created);
      setNotice('LifeObject duplicated as a draft.');
    } catch (duplicateError) {
      setError(duplicateError.message);
    } finally {
      setSaving(false);
    }
  };

  const editLifeObject = (lifeObject) => setEditor({ open: true, lifeObject });

  return (
    <ThemeProvider theme={lifeTaggerTheme}>
      <Box
        className="lt-collection"
        sx={{
          minHeight: '100vh',
          bgcolor: '#FAF9F7',
        }}
      >
        <Box
          component="header"
          sx={{
            height: 80,
            px: { xs: 2.5, md: 6 },
            borderBottom: '1px solid #EAE6DF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              component="img"
              src="/images/lifeTagger_logo.png"
              alt=""
              sx={{ width: 28, height: 28, objectFit: 'contain' }}
            />
            <Typography
              sx={{
                fontFamily: lifeTaggerDesign.type.brand,
                fontStyle: 'italic',
                color: '#294438',
                fontSize: 22,
              }}
            >
              LifeTagger
            </Typography>
          </Stack>
          <Stack direction="row" spacing={{ xs: 1, md: 4 }} alignItems="center">
            <Button
              href="/analytics"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: '#706C65',
                minWidth: 0,
                px: 0,
              }}
            >
              Activity
            </Button>
            <Button
              href="/user/edit"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: '#706C65',
                minWidth: 0,
                px: 0,
              }}
            >
              Settings
            </Button>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <SearchRoundedIcon sx={{ fontSize: 18, color: '#706C65' }} />
            <NotificationsNoneRoundedIcon
              sx={{ fontSize: 18, color: '#706C65' }}
            />
            <Avatar
              src={Parse.User.current()?.get('profile_url')}
              sx={{ width: 32, height: 32 }}
            />
          </Stack>
        </Box>
        <Container
          maxWidth={false}
          sx={{
            width: '100%',
            maxWidth: 1440,
            px: { xs: 2.5, md: 15 },
            pt: { xs: 4, md: 7 },
            pb: 12,
            fontFamily: lifeTaggerDesign.type.body,
          }}
        >
          <Stack spacing={5} className="lt-collection-stack">
            {activeTransport === 'mock' && (
              <Alert severity="info">
                Using contract-accurate sample data until the backend endpoints
                are connected.
              </Alert>
            )}
            {!runtimeReadiness.ready && (
              <Alert severity="error">
                LifeTagger cannot connect yet:{' '}
                {runtimeReadiness.issues.join(' ')}
              </Alert>
            )}
            {error && objects.length > 0 && (
              <Alert severity="error">{error}</Alert>
            )}
            {loading && objects.length > 0 && (
              <LinearProgress aria-label="Loading LifeObjects" />
            )}
            <div className="lt-collection-heading">
              <div>
                <div className="lt-section-eyebrow">YOUR WORLD, CONNECTED</div>
                <h1 className="lt-page-title">
                  The collection
                  <span style={{ color: '#a3ad91', fontStyle: 'italic' }}>
                    .
                  </span>
                </h1>
                <p className="lt-page-description">
                  {collectionTotal}
                  {hasMoreFromServer && serverTotal == null ? '+' : ''}{' '}
                  LifeObjects. Countless ways to connect.
                </p>
              </div>
              <button
                className="lt-button"
                onClick={() => setEditor({ open: true, lifeObject: null })}
              >
                <StudioIcon name="plus" size={17} />
                New LifeObject
              </button>
            </div>
            <section className="lt-collection-hero">
              <div className="lt-collection-hero-copy">
                <span className="lt-section-eyebrow">
                  CONTENT WITH A SENSE OF PLACE
                </span>
                <h2>
                  More than content.
                  <br />
                  <em>A connection waiting to happen.</em>
                </h2>
                <p>
                  Leave a story at a place. A message for a person. A little
                  wonder for someone to discover.
                </p>
              </div>
              <div className="lt-collection-hero-art">
                <span>Some things are meant to be found.</span>
              </div>
            </section>
            <div className="lt-collection-toolbar">
              <div
                className="lt-filter-tabs"
                role="group"
                aria-label="Filter collection"
              >
                {statusOptions.map(([value, label]) => (
                  <button
                    key={value}
                    className={statusFilter === value ? 'active' : ''}
                    aria-pressed={statusFilter === value}
                    onClick={() => {
                      setStatusFilter(value);
                      setSelected(null);
                    }}
                  >
                    {label}
                    {filterCount(value) != null && (
                      <span>{filterCount(value)}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="lt-collection-tools">
                <label className="lt-search">
                  <StudioIcon name="search" size={15} />
                  <input
                    aria-label="Search LifeObjects"
                    placeholder="Find a LifeObject…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
                <select
                  className="lt-sort"
                  aria-label="Sort collection"
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setPage(1);
                  }}
                >
                  {sortOptions.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                <div
                  className="lt-view-toggle"
                  role="group"
                  aria-label="Collection view"
                >
                  {['grid', 'list'].map((mode) => (
                    <button
                      className="lt-icon-button"
                      key={mode}
                      aria-label={mode === 'grid' ? 'Grid view' : 'List view'}
                      aria-pressed={view === mode}
                      onClick={() => setView(mode)}
                    >
                      <StudioIcon name={mode} size={16} />
                    </button>
                  ))}
                </div>
                <button
                  className="lt-icon-button"
                  title="Select multiple LifeObjects"
                  aria-label="Select multiple LifeObjects"
                  aria-pressed={selectionMode}
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    setSelectedIds([]);
                  }}
                >
                  <StudioIcon name="collection" size={16} />
                </button>
              </div>
            </div>
            {hasMoreFromServer && (
              <Typography variant="caption" color="text.secondary">
                Search and filters apply to {objects.length} loaded LifeObjects.
                Load more below to browse the rest of your collection.
              </Typography>
            )}
            {selectedIds.length > 0 && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                spacing={1}
                sx={{
                  position: 'fixed',
                  zIndex: 1300,
                  left: '50%',
                  bottom: 24,
                  transform: 'translateX(-50%)',
                  width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                  p: 1,
                  px: 1.5,
                  bgcolor: lifeTaggerDesign.color.ink,
                  color: '#fff',
                  borderRadius: 999,
                  boxShadow: '0 18px 50px rgba(15,20,25,.28)',
                  '& .MuiButton-root': {
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,.28)',
                  },
                }}
              >
                <Typography sx={{ mr: 'auto' }}>
                  {selectedIds.length} selected
                </Typography>
                <Button size="small" onClick={() => setSelectedIds([])}>
                  Clear
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={() => updateSelectedStatus('paused')}
                >
                  Pause
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={() => updateSelectedStatus('archived')}
                >
                  Archive
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={saving}
                  onClick={() => updateSelectedStatus('draft')}
                >
                  Restore as draft
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving}
                  onClick={() => updateSelectedStatus('active')}
                >
                  Publish
                </Button>
              </Stack>
            )}
            <Box>
              <Grid container spacing={2.25}>
                {loading &&
                  objects.length === 0 &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <Grid item xs={12} sm={6} lg={4} key={`skeleton-${index}`}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          bgcolor: '#fff',
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          height={190}
                          animation="wave"
                        />
                        <Stack spacing={1} sx={{ p: 1.75 }}>
                          <Skeleton width="44%" height={28} />
                          <Skeleton width="92%" />
                          <Skeleton width="70%" />
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                {!loading && error && objects.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ py: 10, px: 3, textAlign: 'center' }}>
                      <Typography
                        variant="h4"
                        sx={{ fontFamily: lifeTaggerDesign.type.headline }}
                      >
                        We couldn’t load your collection.
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                        Your LifeObjects are still safe. Check the connection
                        and try again.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => setReloadKey((value) => value + 1)}
                      >
                        Retry
                      </Button>
                    </Box>
                  </Grid>
                )}
                {pageObjects.length > 0 && (
                  <Grid item xs={12}>
                    <EditorialCollectionStream
                      view={view}
                      items={pageObjects}
                      loading={loading}
                      selectedIds={selectedIds}
                      onSelect={setSelected}
                      onDelete={setDeleteTarget}
                      onToggleBulk={toggleBulkSelection}
                      selectionMode={selectionMode}
                      onEdit={(lifeObject) =>
                        setEditor({ open: true, lifeObject })
                      }
                    />
                  </Grid>
                )}
                {!loading && !error && visibleObjects.length === 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        py: 8,
                        px: 3,
                        textAlign: 'center',
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 3,
                      }}
                    >
                      {objects.length === 0 &&
                      !search &&
                      statusFilter === 'all' ? (
                        <>
                          <Typography
                            variant="h4"
                            sx={{
                              fontFamily: lifeTaggerDesign.type.headline,
                            }}
                          >
                            Your collection begins here.
                          </Typography>
                          <Typography
                            color="text.secondary"
                            sx={{ mt: 1, mb: 3 }}
                          >
                            Start mapping the places, objects, and experiences
                            that matter to your organization.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddRoundedIcon />}
                            onClick={() =>
                              setEditor({ open: true, lifeObject: null })
                            }
                          >
                            Create first LifeObject
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="h4"
                            sx={{
                              fontFamily: lifeTaggerDesign.type.headline,
                            }}
                          >
                            No LifeObjects found.
                          </Typography>
                          <Typography
                            color="text.secondary"
                            sx={{ mt: 1, mb: 3 }}
                          >
                            Nothing matches the current search and filters.
                          </Typography>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent="center"
                          >
                            {search && (
                              <Button
                                variant="outlined"
                                onClick={() => setSearch('')}
                              >
                                Clear search
                              </Button>
                            )}
                            {statusFilter !== 'all' && (
                              <Button
                                variant="outlined"
                                onClick={() => setStatusFilter('all')}
                              >
                                Reset filters
                              </Button>
                            )}
                          </Stack>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
              {visibleObjects.length > 0 && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ mt: 4, pt: 3, borderTop: '1px solid #E4E1DA' }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Showing {Math.min(pageSize, pageObjects.length)} of{' '}
                    {collectionTotal}
                    {hasMoreFromServer && serverTotal == null ? '+' : ''}{' '}
                    LifeObjects
                  </Typography>
                  <Pagination
                    page={page}
                    count={pageCount}
                    onChange={(_, nextPage) => setPage(nextPage)}
                    shape="rounded"
                  />
                </Stack>
              )}
              {hasMoreFromServer && (
                <Stack alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={loading}
                    onClick={loadMoreFromServer}
                  >
                    {loading ? 'Loading more…' : 'Load more LifeObjects'}
                  </Button>
                </Stack>
              )}
              {loadMoreError && (
                <Stack alignItems="center" spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    We couldn’t load more LifeObjects. Everything already shown
                    is still available.
                  </Typography>
                  <Button variant="outlined" onClick={loadMoreFromServer}>
                    Retry loading more
                  </Button>
                </Stack>
              )}
            </Box>
          </Stack>
          <Drawer
            anchor="right"
            open={Boolean(selected)}
            onClose={() => setSelected(null)}
            PaperProps={{
              sx: {
                width: { xs: '100%', md: '92vw' },
                bgcolor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            {selected && (
              <LifeObjectPreviewOverlay
                lifeObject={selected}
                previewUrl={
                  previewIdentifier
                    ? `/${
                        apiCapabilities.contextResolution
                          ? 'experience/lifecode'
                          : 'qrtags'
                      }/${encodeURIComponent(previewIdentifier)}`
                    : null
                }
                onClose={() => setSelected(null)}
                onEdit={editLifeObject}
                onDuplicate={duplicateSelected}
                onDelete={setDeleteTarget}
                saving={saving}
              />
            )}
          </Drawer>
          <ObjectEditor
            open={editor.open}
            lifeObject={editor.lifeObject}
            initialAsset={editor.initialAsset}
            initialDiscovery={editor.initialDiscovery}
            onClose={() => setEditor({ open: false, lifeObject: null })}
            onSave={saveObject}
            saving={saving}
          />
          <Dialog
            open={Boolean(deleteTarget)}
            onClose={() => !saving && setDeleteTarget(null)}
            aria-labelledby="delete-lifeobject-title"
          >
            <DialogTitle id="delete-lifeobject-title">
              Delete this LifeObject?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                “{deleteTarget?.name}” will be removed from your collection and
                will no longer be discoverable. Its content will be archived,
                not permanently erased.
              </DialogContentText>
              {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={deleteObject}
                color="error"
                variant="contained"
                disabled={saving}
              >
                {saving ? 'Deleting…' : 'Delete LifeObject'}
              </Button>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={Boolean(notice)}
            autoHideDuration={3500}
            onClose={() => setNotice('')}
            message={notice}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
}
